// 📄 pages/Proyectos/ProyectoGeneralHome.tsx
// Portada de un proyecto SIN el módulo SAP Ariba (plan-proyectos-generales.md,
// G1): accesos a "Gestión del proyecto". Los proyectos SAP siguen entrando a
// ProjectSolutionsHome.tsx. Mismo filtro que el menú lateral: sin ítems
// soloConsultor para el rol cliente, ni secciones ocultas para este miembro.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../../context/ProjectContext.tsx';
import { useAuth } from '../../context/AuthContext.tsx';
import { getMemberPermisos } from '../../services/projectService.ts';
import { PROYECTO_ITEMS, HERRAMIENTAS_ITEMS, funcionalidadesItems, segunModulos, IconoDe, MenuItemDef } from '../Dashboard/menuConfig.tsx';
import HomeCard from '../../components/common/HomeCard.tsx';
import { porcentajeAvance } from '../../services/hitoService.ts';

const TAGLINES: Record<string, string> = {
  detalle: 'Datos del proyecto, cliente y equipo.',
  hitos: 'Fases y entregables del proyecto, con su avance.',
  cambios: 'Cambios pedidos durante el proyecto, con su impacto y aprobación.',
  versiones: 'Entregas del proyecto con sus cambios, tareas y notas de versión.',
  aprendizaje: 'Guías para los usuarios finales: cómo usar lo que se entrega.',
  tareas: 'Pendientes del equipo, asignables también al cliente.',
  incidentes: 'Problemas reportados y su seguimiento.',
  pruebas: 'Casos de prueba del proyecto.',
  especificaciones: 'Documentos del proyecto (funcionales, técnicos, manuales), con adjuntos.',
  credenciales: 'Accesos del proyecto, cifrados y con aprobación para verlos.',
  miembros: 'Quién participa y con qué rol.',
  organigrama: 'Cargos y responsabilidades del equipo y del cliente.',
  horas: 'Registro de horas trabajadas.',
  ausencias: 'Vacaciones y permisos del equipo.',
  'mi-espacio': 'Tus notas y archivos personales de este proyecto.'
};

export default function ProyectoGeneralHome() {
  const { project, projectId, esCliente } = useProject();
  const avance = project?.avanceHitos;
  const porcentaje = porcentajeAvance(avance);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ocultas, setOcultas] = useState<string[]>([]);

  useEffect(() => {
    if (!user || !projectId) return;
    getMemberPermisos(projectId, user.uid)
      .then((p) => setOcultas(p?.seccionesOcultas || []))
      .catch(() => setOcultas([]));
  }, [projectId, user]);

  const items: MenuItemDef[] = [
    ...PROYECTO_ITEMS.filter((i) => ['hitos', 'cambios', 'versiones', 'aprendizaje', 'tareas', 'incidentes', 'detalle'].includes(i.id)),
    ...segunModulos(funcionalidadesItems(false, false, false), false).map((i) => (i.id === 'especificaciones' ? { ...i, label: 'Documentación' } : i)),
    ...segunModulos(HERRAMIENTAS_ITEMS, false),
    ...PROYECTO_ITEMS.filter((i) => !['hitos', 'cambios', 'versiones', 'aprendizaje', 'tareas', 'incidentes', 'detalle'].includes(i.id))
  ].filter((i) => (!i.soloConsultor || !esCliente) && !ocultas.includes(i.id));

  return (
    <div className="space-y-6">
      {avance && avance.total > 0 && (
        <button
          type="button"
          onClick={() => navigate(`/proyecto/${projectId}/hitos`)}
          className="w-full text-left bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl p-5 space-y-2 transition-all"
        >
          <div className="flex items-end justify-between">
            <p className="text-sm text-gray-300">Avance del proyecto</p>
            <p className="text-2xl font-light text-white">{porcentaje}%</p>
          </div>
          <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full" style={{ width: `${porcentaje}%` }} />
          </div>
          <p className="text-xs text-gray-500">{avance.completados} de {avance.total} hitos completados</p>
        </button>
      )}
      <p className="text-gray-400">Gestión del proyecto:</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((item) => (
          <HomeCard
            key={item.id}
            icon={<IconoDe icon={item.icon} size={32} />}
            label={item.label}
            tagline={TAGLINES[item.id] || ''}
            onClick={() => navigate(`/proyecto/${projectId}/${item.id}`)}
          />
        ))}
      </div>
    </div>
  );
}
